import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextInput } from "components/inputs";
import React, { useCallback, useMemo } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useFormContext } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { setFocusedField } from "reducers/main";
import { api } from "services/api";
import { useCreateClientMutation } from "services/client-api";
import { useDispatch } from "utils/redux";

import KaboodleModal from "./KaboodleModal";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  clientName: string;
  clientId: string;
};

const CreateClientModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    setError,
  } = useFormContext<FormData>();

  const [createClient] = useCreateClientMutation();

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        const result = await createClient({
          data: { name: data.clientName, client_id: data.clientId },
        }).unwrap();

        if (result.id) {
          dispatch(api.util.invalidateTags(["Clients"]));
          reset();
          onClose();
          if (location.pathname.includes("client"))
            navigate(`${location.pathname}/${result.id}/overview`);
        }
      } catch (error) {
        if (error.data?.name) {
          const message = error.data.name;
          setError("clientName", {
            type: error.status || "error",
            message: Array.isArray(message) ? message[0] : message,
          });
          KaboodleToaster.error(Array.isArray(message) ? message[0] : message);
          // set focused field to show red border
          dispatch(setFocusedField("clientName"));
        } else if (error.data?.client_id) {
          const message = error.data.client_id;
          setError("clientId", {
            type: error.status || "error",
            message: Array.isArray(message) ? message[0] : message,
          });
          KaboodleToaster.error(Array.isArray(message) ? message[0] : message);
          dispatch(setFocusedField("clientId"));
        } else {
          KaboodleToaster.error("Failed to create client");
          console.error("Failed to create client:", error);
        }
      }
    },
    [createClient, reset, onClose, navigate, dispatch, location, setError],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
    dispatch(setFocusedField(null));
  }, [reset, onClose, dispatch]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create Client">
        <Controller
          control={control}
          name="clientName"
          rules={{ required: "Client name is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Name"
              label="Client Name"
              errors={errors}
            />
          )}
        />
        <Controller
          control={control}
          name="clientId"
          rules={{ required: "Client ID is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Client ID"
              label="Client ID"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateClientModal;
